import styled from '@emotion/styled';
import { find } from 'linkifyjs';
import { useCallback, useEffect, useId, useRef } from 'react';

const supportsHighlightAPI = typeof CSS !== 'undefined' && 'highlights' in CSS;

/* v8 ignore start -- CSS Highlight API functions only execute in browsers, not JSDOM */
let styleInjected = false;
function injectHighlightStyle() {
    if (styleInjected || !supportsHighlightAPI) return;
    const style = document.createElement('style');
    style.textContent = `::highlight(linkify) { color: #2563eb; }`;
    document.head.appendChild(style);
    styleInjected = true;
}

const allInstanceRanges = new Map<string, Range[]>();
let sharedHighlight: Highlight | null = null;

function getSharedHighlight(): Highlight | null {
    if (!supportsHighlightAPI) return null;
    if (!sharedHighlight) {
        sharedHighlight = new Highlight();
        CSS.highlights.set('linkify', sharedHighlight);
    }
    return sharedHighlight;
}

function syncHighlight() {
    const highlight = getSharedHighlight();
    if (!highlight) return;
    highlight.clear();
    for (const ranges of allInstanceRanges.values()) {
        for (const range of ranges) highlight.add(range);
    }
}

function charOffsetToPosition(
    container: HTMLElement,
    offset: number,
): { node: Node; offset: number } | null {
    let remaining = offset;

    function walk(node: Node): { node: Node; offset: number } | null {
        if (node.nodeType === Node.TEXT_NODE) {
            const len = (node.textContent ?? '').length;
            if (remaining <= len) return { node, offset: remaining };
            remaining -= len;
        } else if (node.nodeName === 'BR') {
            if (remaining === 0) return { node: node.parentNode!, offset: indexOf(node) + 1 };
            remaining -= 1;
        } else {
            for (let i = 0; i < node.childNodes.length; i++) {
                const result = walk(node.childNodes[i]);
                if (result) return result;
            }
        }
        return null;
    }

    return walk(container);
}

function indexOf(node: Node): number {
    let i = 0;
    let sibling = node.previousSibling;
    while (sibling) {
        i++;
        sibling = sibling.previousSibling;
    }
    return i;
}

function charOffsetToRange(container: HTMLElement, start: number, end: number): Range | null {
    const startPos = charOffsetToPosition(container, start);
    const endPos = charOffsetToPosition(container, end);
    if (!startPos || !endPos) return null;

    const range = document.createRange();
    range.setStart(startPos.node, startPos.offset);
    range.setEnd(endPos.node, endPos.offset);
    return range;
}
/* v8 ignore stop */

const getTextContent = (el: HTMLElement): string => {
    let text = '';
    for (let i = 0; i < el.childNodes.length; i++) {
        const node = el.childNodes[i];
        if (node.nodeType === Node.TEXT_NODE) {
            text += node.textContent ?? '';
        } else if (node.nodeName === 'BR') {
            text += '\n';
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            const tag = node.nodeName;
            if ((tag === 'DIV' || tag === 'P') && text.length > 0 && !text.endsWith('\n')) {
                text += '\n';
            }
            text += getTextContent(node as HTMLElement);
            if ((tag === 'DIV' || tag === 'P') && i < el.childNodes.length - 1) {
                if (!text.endsWith('\n')) text += '\n';
            }
        }
    }
    return text;
};

const Editable = styled.div<{ hasError?: boolean; disabled?: boolean }>`
    position: relative;
    max-width: 100%;
    max-height: 100%;
    width: 100%;
    min-height: 90px;
    padding: 10px 12px;
    border: 1px solid ${({ hasError }) => (hasError ? '#dc2626' : '#d1d5db')};
    border-radius: 6px;
    font-family: inherit;
    font-size: 13px;
    line-height: 1.5;
    text-align: left;
    box-sizing: border-box;
    white-space: pre-wrap;
    word-wrap: break-word;
    overflow-y: auto;
    resize: both;
    outline: none;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'text')};
    background: ${({ disabled }) => (disabled ? '#f3f4f6' : 'transparent')};
    opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

    &:focus {
        border-color: ${({ hasError }) => (hasError ? '#dc2626' : '#2563eb')};
        box-shadow: 0 0 0 2px ${({ hasError }) => (hasError ? '#fecaca' : '#bfdbfe')};
    }

    &:empty::before {
        content: attr(data-placeholder);
        color: #9ca3af;
        pointer-events: none;
    }
`;

interface LinkifyTextareaProps {
    id?: string;
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    hasError?: boolean;
    disabled?: boolean;
    'data-testid'?: string;
    spellCheck?: boolean;
}

export const LinkifyTextarea = ({
    id,
    value,
    onChange,
    placeholder,
    hasError,
    disabled,
    'data-testid': dataTestId,
    spellCheck,
}: LinkifyTextareaProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const instanceId = useId();
    const lastValue = useRef(value);
    const rafId = useRef<number>(0);

    /* v8 ignore start */
    const updateHighlightsSync = useCallback(() => {
        const el = ref.current;
        if (!el || !supportsHighlightAPI) return;

        const text = getTextContent(el);
        const matches = find(text);
        const ranges: Range[] = [];

        for (const match of matches) {
            const range = charOffsetToRange(el, match.start, match.end);
            if (range) ranges.push(range);
        }

        allInstanceRanges.set(instanceId, ranges);
        syncHighlight();
    }, [instanceId]);
    /* v8 ignore stop */

    const updateHighlights = useCallback(() => {
        cancelAnimationFrame(rafId.current);
        rafId.current = requestAnimationFrame(updateHighlightsSync);
    }, [updateHighlightsSync]);

    useEffect(() => {
        injectHighlightStyle();
        return () => {
            cancelAnimationFrame(rafId.current);
            allInstanceRanges.delete(instanceId);
            syncHighlight();
        };
    }, [instanceId]);

    const setContent = useCallback(
        (text: string) => {
            if (!ref.current) return;
            if (!text) {
                ref.current.innerHTML = '<br>';
            } else {
                const escaped = text
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/\n/g, '<br>');
                ref.current.innerHTML = text.endsWith('\n') ? escaped + '<br>' : escaped;
            }
            updateHighlightsSync();
        },
        [updateHighlightsSync],
    );

    useEffect(() => {
        setContent(value);
    }, []);

    useEffect(() => {
        if (value !== lastValue.current) {
            lastValue.current = value;
            setContent(value);
        }
    }, [value, setContent]);

    const handleInput = useCallback(() => {
        if (!ref.current) return;
        const text = getTextContent(ref.current);
        lastValue.current = text;
        onChange(text);
        updateHighlights();
    }, [onChange, updateHighlights]);

    const handlePaste = useCallback((e: React.ClipboardEvent) => {
        e.preventDefault();
        document.execCommand('insertText', false, e.clipboardData.getData('text/plain'));
    }, []);

    return (
        <Editable
            ref={ref}
            id={id}
            contentEditable={!disabled}
            role="textbox"
            aria-multiline="true"
            aria-disabled={disabled || undefined}
            data-testid={dataTestId}
            data-placeholder={placeholder}
            spellCheck={spellCheck}
            onInput={handleInput}
            onPaste={handlePaste}
            hasError={hasError}
            disabled={disabled}
            suppressContentEditableWarning
        />
    );
};
